import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import FORMATTERS from 'helpers/formatters';
import { FloatingCard, Button } from '_components/_core';

import { FormSchema } from './utilities';
import { StyledButton } from './styles';
import { ChargeDetails } from './components';

function ChargeForm({
  isOpen,
  isEditing,
  isCreating,
  charge_id,
  charge: oldCharge,
  title,
  side,

  onToggleForm,
  onBeforeSaveCallback,
  onAfterSaveCallback,
  onAfterSaveCallbackWithReset,

  onFetchCharge,
  onCreateCharge,
  onEditCharge,
  onDeleteCharge,

  paymentMethods,
  paymentGateways,
  recipients,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [newCharge, setNewCharge] = useState({});
  const [activeTab, setActiveTab] = useState('MAIN');

  const charge = useMemo(() => {
    if (charge_id) {
      return newCharge;
    }

    return oldCharge;
  }, [charge_id, newCharge, oldCharge]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('MAIN');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!charge_id) {
      return;
    }

    setIsLoading(true);

    onFetchCharge(charge_id, found_charge => {
      setNewCharge(found_charge);

      setIsLoading(false);
    });
  }, [charge_id, onFetchCharge, isOpen]);

  const initialValues = useMemo(() => {
    if (charge) {
      return {
        id: charge.id,
        company_payment_gateway_id: charge.company_payment_gateway_id,
        payment_method_payment_gateway_id: charge.payment_method_payment_gateway_id,
        amount: charge.amount,
        description: charge.description,
        due_date: charge.due_date,
        charge_type: charge.charge_type,
        recurrence_cycle: charge.recurrence_cycle,
        total_installments: charge.total_installments,
        recipient_id: charge.recipient_id,
        is_recurring: charge.charge_type === 'RECURRING',
      };
    }

    return {
      id: null,
      company_payment_gateway_id: null,
      payment_method_payment_gateway_id: null,
      amount: null,
      description: null,
      due_date: null,
      charge_type: 'SINGLE',
      recurrence_cycle: 'MONTHLY',
      total_installments: 1,
      is_recurring: false,
    };
  }, [charge]);

  const handleSubmitCharge = useCallback(
    (values, { resetForm }) => {
      setIsLoading(true);

      if (onBeforeSaveCallback) {
        onBeforeSaveCallback(values);
      }

      const errorCallback = () => {
        setIsLoading(false);
      };

      if (values.id) {
        onEditCharge(
          values.id,
          values,
          data => {
            if (onAfterSaveCallback) {
              onAfterSaveCallback(data);
            }

            if (onAfterSaveCallbackWithReset) {
              onAfterSaveCallbackWithReset(data);

              resetForm();
              onToggleForm();
            }

            if (!onBeforeSaveCallback && !onAfterSaveCallback) {
              resetForm();
              onToggleForm();
            }

            setIsLoading(false);
          },
          errorCallback,
        );
      } else {
        onCreateCharge(
          values,
          data => {
            if (onAfterSaveCallback) {
              onAfterSaveCallback(data);
            }

            if (onAfterSaveCallbackWithReset) {
              onAfterSaveCallbackWithReset(data);

              resetForm();
              onToggleForm();
            }

            if (!onBeforeSaveCallback && !onAfterSaveCallback) {
              resetForm();
              onToggleForm();
            }

            setIsLoading(false);
          },
          errorCallback,
        );
      }
    },
    [
      onCreateCharge,
      onEditCharge,
      onToggleForm,
      onBeforeSaveCallback,
      onAfterSaveCallback,
      onAfterSaveCallbackWithReset,
    ],
  );

  const handleDeleteCharge = useCallback(() => {
    onDeleteCharge(charge, () => {
      if (onAfterSaveCallback) {
        onAfterSaveCallback();
        return;
      }

      onToggleForm();
    });
  }, [onDeleteCharge, onAfterSaveCallback, onToggleForm, charge]);

  const renderFooter = useCallback(
    (handleSubmit, isValid) => (
      <div className="d-flex">
        <StyledButton
          variant="success"
          className="mr-2 flex-fill"
          isLoading={isEditing || isCreating || isLoading}
          disabled={!isValid}
          onClick={handleSubmit}
          type="submit"
          id="btn-c-form-save"
        >
          Salvar
        </StyledButton>
        <Button
          id="btn-c-form-cancel"
          className="flex-fill"
          variant="inverse-dark"
          onClick={onToggleForm}
        >
          Cancelar
        </Button>
      </div>
    ),
    [onToggleForm, isEditing, isCreating, isLoading],
  );

  const renderDeleteButton = useCallback(() => {
    if (!charge || !charge.id) return null;

    return (
      <div className="d-flex align-items-center justify-content-between mb-1 mt-1">
        <small className="ml-3 text-muted">
          {charge &&
            charge.id &&
            `Criado em: ${FORMATTERS.DATE_DDMMYYYYHHMM(charge.created_at)}`}
        </small>
        <Button
          variant="link"
          className="text-danger"
          type="submit"
          onClick={handleDeleteCharge}
          id="btn-delete-charge"
        >
          Excluir
        </Button>
      </div>
    );
  }, [handleDeleteCharge, charge]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmitCharge}
      validationSchema={FormSchema}
    >
      {({ handleSubmit, setFieldValue, values, isValid, errors }) => (
        <FloatingCard
          title={title}
          fullHeight
          isVisible={isOpen}
          onToggleVisibility={onToggleForm}
          beforeFooterContent={renderDeleteButton()}
          footerContent={renderFooter(handleSubmit, isValid)}
          className={isOpen ? 'charge-form-open' : 'charge-form-closed'}
          bodyClassName="p-0"
          withCloseButton
          width={900}
          side={side}
        >
          <ChargeDetails
            handleSubmit={handleSubmit}
            setFieldValue={setFieldValue}
            values={values}
            paymentMethods={paymentMethods}
            paymentGateways={paymentGateways}
            recipients={recipients}
            errors={errors}
          />
        </FloatingCard>
      )}
    </Formik>
  );
}

ChargeForm.defaultProps = {
  charge: {},
  onBeforeSaveCallback: null,
  onAfterSaveCallback: null,
  isEditing: false,
  isCreating: false,
  title: 'Criar Cobrança',
  side: 'left',
  charge_id: null,
  onAfterSaveCallbackWithReset: null,
  recipients: [],
};

ChargeForm.propTypes = {
  title: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
  isCreating: PropTypes.bool,
  charge: PropTypes.object,
  side: PropTypes.string,
  charge_id: PropTypes.string,
  onToggleForm: PropTypes.func.isRequired,
  onCreateCharge: PropTypes.func.isRequired,
  onEditCharge: PropTypes.func.isRequired,
  onBeforeSaveCallback: PropTypes.func,
  onAfterSaveCallback: PropTypes.func,
  onDeleteCharge: PropTypes.func.isRequired,
  onFetchCharge: PropTypes.func.isRequired,
  onAfterSaveCallbackWithReset: PropTypes.func,
  paymentMethods: PropTypes.array.isRequired,
  paymentGateways: PropTypes.array.isRequired,
  onFetchPaymentMethods: PropTypes.func.isRequired,
  onFetchPaymentGateways: PropTypes.func.isRequired,
  recipients: PropTypes.array.isRequired,
};

export default ChargeForm;
